import { internalAxios } from '@/helpers/http/internalAxiosClient';
import {
  ContactFormData,
  ContactFormSubmitStatus,
  ContactRequestResponse,
} from '@/store/types/contact-form-state';
import { defineStore } from 'pinia';
import { ref } from 'vue';

const EMAIL_VERIFICATION_OPT_IN_QUERY = '?emailVerification=enabled';

const buildContactRequestEndpoint = (shouldOptInToEmailVerification?: boolean): string => {
  const contactRequestEndpoint = `${import.meta.env.VUE_CONTACT_SERVICE_API_ENDPOINT}/contact-request`;

  return shouldOptInToEmailVerification
    ? `${contactRequestEndpoint}${EMAIL_VERIFICATION_OPT_IN_QUERY}`
    : contactRequestEndpoint;
};

export const useContactFormStore = defineStore('contactForm', () => {
  const contactFormSubmitStatus = ref<ContactFormSubmitStatus>(
    ContactFormSubmitStatus.NOT_SUBMITTED,
  );
  const contactFormSubmitError = ref<Error | null>(null);
  const contactInquiryId = ref<string | undefined>(undefined);
  const isSubmittedAsAddressRequest = ref<boolean>(false);
  const isContactRequestEmailVerificationRequired = ref<boolean>(false);
  const contactRequestEmail = ref<string | undefined>(undefined);
  const isSubmittedByAuthenticatedUser = ref<boolean>(false);
  const emailVerificationResendStatus = ref<string | undefined>(undefined);

  const contactFormSubmitStatusChange = (newState: ContactFormSubmitStatus) => {
    contactFormSubmitStatus.value = newState;
  };
  const contactFormSubmitErrorChange = (error: Error | null) => {
    contactFormSubmitError.value = error;
  };
  const contactInquiryIdChange = (id: string | undefined) => {
    contactInquiryId.value = id;
  };
  const isSubmittedAsAddressRequestChange = (submittedAsAddressRequest: boolean) => {
    isSubmittedAsAddressRequest.value = submittedAsAddressRequest;
  };
  const contactRequestEmailVerificationRequiredChange = (isRequired: boolean) => {
    isContactRequestEmailVerificationRequired.value = isRequired;
  };
  const contactRequestEmailChange = (email: string | undefined) => {
    contactRequestEmail.value = email;
  };
  const isSubmittedByAuthenticatedUserChange = (isAuthenticated: boolean) => {
    isSubmittedByAuthenticatedUser.value = isAuthenticated;
  };

  const sendContactRequest = async ({
    id,
    contactInformation,
    submittedAsAddressRequest,
    isTenantPlusSubscriber,
    profileId,
    accessToken,
    shouldOptInToEmailVerification,
    isInEmailVerificationCodeExperiment,
  }: {
    id: string | undefined;
    contactInformation: ContactFormData;
    submittedAsAddressRequest: boolean;
    isTenantPlusSubscriber: boolean;
    profileId?: string;
    accessToken?: string;
    shouldOptInToEmailVerification?: boolean;
    isInEmailVerificationCodeExperiment?: boolean;
  }): Promise<void> => {
    try {
      isSubmittedAsAddressRequestChange(submittedAsAddressRequest);
      contactRequestEmailChange(contactInformation.email);
      isSubmittedByAuthenticatedUserChange(Boolean(accessToken));

      const requestHeaders = {} as Record<string, string>;
      if (accessToken) requestHeaders.Authorization = `Bearer ${accessToken}`;

      const result = await internalAxios.post<ContactRequestResponse>(
        buildContactRequestEndpoint(shouldOptInToEmailVerification),
        {
          id,
          isTenantPlusUser: isTenantPlusSubscriber,
          profileId,
          ...contactInformation,
          ...(isInEmailVerificationCodeExperiment
            ? { isInEmailVerificationCodeExperiment: true }
            : {}),
        },
        {
          headers: requestHeaders,
        },
      );
      const inquiryId = result.data.contactRequest.id;

      contactFormSubmitErrorChange(null);
      contactInquiryIdChange(inquiryId);
      const isEmailVerificationRequired = result.data.emailVerification?.required === true;
      contactRequestEmailVerificationRequiredChange(isEmailVerificationRequired);
      contactFormSubmitStatusChange(
        isEmailVerificationRequired
          ? ContactFormSubmitStatus.EMAIL_VERIFICATION_REQUIRED
          : ContactFormSubmitStatus.SUCCESS,
      );
    } catch (error) {
      let submitStatus = ContactFormSubmitStatus.ERROR;
      if (error.response?.status === 404) {
        submitStatus = ContactFormSubmitStatus.LISTING_OFFLINE;
      }

      contactFormSubmitErrorChange(error);
      contactInquiryIdChange(undefined);
      contactRequestEmailVerificationRequiredChange(false);
      contactFormSubmitStatusChange(submitStatus);
    }
  };

  const resendConfirmationLink = async (contactRequestId: string): Promise<void> => {
    const endpoint = `${import.meta.env.VUE_CONTACT_SERVICE_API_ENDPOINT}/contact-request/email-verification/resend`;

    try {
      const result = await internalAxios.post<{ status: string }>(endpoint, {
        contactRequestId,
      });
      emailVerificationResendStatus.value = result.data.status;
    } catch (error) {
      emailVerificationResendStatus.value = error.response?.data?.status ?? 'UNEXPECTED_ERROR';
    }
  };

  return {
    contactFormSubmitStatus: contactFormSubmitStatus,
    contactFormSubmitError: contactFormSubmitError,
    contactInquiryId: contactInquiryId,
    isSubmittedAsAddressRequest: isSubmittedAsAddressRequest,
    isContactRequestEmailVerificationRequired,
    contactRequestEmail,
    isSubmittedByAuthenticatedUser,
    emailVerificationResendStatus,
    contactFormSubmitStatusChange,
    sendContactRequest,
    resendConfirmationLink,
  };
});
